import {
  useEffect,
  useState,
} from 'react';

import {
  useQuery,
} from '@tanstack/react-query';

import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  getMarginCenterDynamicComparables,
} from '../services/marginCenterService';

import type {
  MarginCenterDynamicComparablesDistribution,
} from '../types/dynamicComparables';

interface Props {
  entityType: 'product' | 'customer';
  attributes: Record<string, unknown>;
  contextEntityId: string;
  startDate: string | null;
  endDate: string | null;
}

const numberFormat = new Intl.NumberFormat('es-ES', {
  maximumFractionDigits: 2,
});

function formatNumber(value: number | null | undefined): string {
  return value === null || value === undefined
    ? '—'
    : numberFormat.format(value);
}

function formatPct(value: number | null | undefined): string {
  return value === null || value === undefined
    ? '—'
    : `${numberFormat.format(value)} %`;
}

function formatMoney(
  value: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (value === null || value === undefined || !currency) {
    return '—';
  }

  try {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${numberFormat.format(value)} ${currency}`;
  }
}

function formatRange(
  distribution: MarginCenterDynamicComparablesDistribution | undefined,
  format: (value: number | null | undefined) => string,
): string {
  if (
    !distribution
    || distribution.p25 === null
    || distribution.p75 === null
  ) {
    return '—';
  }

  return `${format(distribution.p25)} – ${format(distribution.p75)}`;
}

export default function PricingDecisionDynamicComparablesPanel({
  entityType,
  attributes,
  contextEntityId,
  startDate,
  endDate,
}: Props) {
  const [cursor, setCursor] = useState<string | null>(null);
  const [previousCursors, setPreviousCursors] = useState<string[]>([]);

  useEffect(() => {
    setCursor(null);
    setPreviousCursors([]);
  }, [entityType, attributes, contextEntityId, startDate, endDate]);

  const request = {
    version: '1.0' as const,
    entity_type: entityType,
    attributes,
    start_date: startDate,
    end_date: endDate,
    scope: 'context' as const,
    context_entity_id: contextEntityId,
    limit: 10,
    cursor,
  };

  const query = useQuery({
    queryKey: ['margin-center', 'dynamic-comparables', request],
    queryFn: () => getMarginCenterDynamicComparables(request),
    enabled: Boolean(contextEntityId),
    staleTime: 5 * 60 * 1000,
  });

  const result = query.data;
  const currency = result?.quality?.currency_code ?? null;
  const isProduct = entityType === 'product';
  const matching = result?.matching;
  const quality = result?.quality;
  const population = result?.population;
  const reference = result?.reference;
  const distribution = result?.distribution;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        overflow: 'hidden',
        border: '1px solid rgba(56,189,248,0.16)',
        bgcolor: 'rgba(8,17,31,0.64)',
      }}
    >
      <Box sx={{ p: { xs: 1.5, md: 1.75 } }}>
        <Typography
          variant="overline"
          sx={{ color: '#7dd3fc', fontWeight: 850, letterSpacing: 0.7 }}
        >
          Comparables comerciales · entidad nueva
        </Typography>
        <Typography variant="subtitle1" sx={{ color: '#f8fafc', fontWeight: 800 }}>
          {isProduct
            ? 'Productos existentes comprados por este cliente'
            : 'Clientes existentes que compran este producto'}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: '#94a3b8' }}>
          Coincidencia exacta por atributos persistidos y disponibles de la
          cuenta. No se utiliza similitud estimada ni se relajan criterios.
          El resultado es evidencia observada, no una predicción de demanda.
        </Typography>
      </Box>

      <Divider />

      {query.isPending && (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Consultando comparables en Supabase…
          </Typography>
        </Box>
      )}

      {query.isError && (
        <Alert severity="warning" sx={{ m: 1.5 }}>
          No se pudieron consultar los comparables. La recomendación de
          optimización sigue disponible; estos datos son complementarios.
        </Alert>
      )}

      {result && (
        <Box sx={{ p: { xs: 1.5, md: 1.75 } }}>
          <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
            {(matching?.used_attributes ?? []).map((attribute) => (
              <Chip
                key={attribute.name}
                size="small"
                label={`${attribute.name}: ${attribute.value} · cobertura ${formatPct(attribute.dimension_coverage_pct)}`}
                sx={{ bgcolor: 'rgba(56,189,248,0.12)', color: '#bae6fd' }}
              />
            ))}
          </Stack>

          {(matching?.ignored_attributes?.length ?? 0) > 0 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#94a3b8' }}>
              Atributos no utilizados:{' '}
              {matching?.ignored_attributes.map((attribute) =>
                `${attribute.name} (${attribute.reason})`).join(', ')}
            </Typography>
          )}

          {result.status === 'insufficient_dimensions' && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Los atributos aportados no coinciden con dimensiones BI utilizables
              en esta cuenta. No se ha inventado un grupo comparable.
            </Alert>
          )}
          {result.status === 'empty_dataset' && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Esta cuenta todavía no dispone de transacciones para comparar.
            </Alert>
          )}
          {result.status === 'context_not_found' && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              No se encuentra la entidad conocida que delimita la comparación.
            </Alert>
          )}
          {result.status === 'no_peers' && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Hay criterios de comparación válidos, pero ninguna entidad
              coincidente registró compras de esta relación contextual en el periodo.
            </Alert>
          )}

          {population && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
              <Chip size="small" variant="outlined" label={`${formatNumber(population.matched_entities)} coincidencias en el catálogo`} />
              <Chip size="small" variant="outlined" label={`${formatNumber(population.active_entities)} con actividad en este contexto`} />
              <Chip size="small" variant="outlined" label={`${formatNumber(population.transaction_rows)} transacciones`} />
            </Stack>
          )}

          {result.status === 'ok' && quality && reference && (
            <>
              {quality.small_sample && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Muestra pequeña: estas referencias son descriptivas y no
                  bastan para afirmar que el comportamiento se repetirá.
                </Alert>
              )}
              {quality.currency_status !== 'single' && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Moneda {quality.currency_status === 'mixed'
                    ? 'heterogénea'
                    : quality.currency_status === 'partial'
                      ? 'incompleta'
                      : 'desconocida'}: no se publican precios, ingresos ni
                  márgenes agregados sin una moneda homogénea y explícita.
                </Alert>
              )}
              {!quality.unit_comparable && isProduct && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Las unidades de medida no son homogéneas o están incompletas;
                  se ocultan las referencias de precio y volumen unitario.
                </Alert>
              )}

              <Box
                sx={{
                  mt: 1.5,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' },
                  gap: 1,
                }}
              >
                {[
                  ['Precio neto medio', formatMoney(reference.average_net_unit_price, currency)],
                  ['Margen agregado', formatPct(reference.margin_percentage)],
                  ['Unidades', formatNumber(reference.units)],
                  ['Cobertura de costes', formatPct(quality.cost_coverage_pct)],
                ].map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{ p: 1.2, borderRadius: 2, border: '1px solid rgba(148,163,184,0.14)' }}
                  >
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      {label}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Typography variant="body2" sx={{ mt: 1.5, color: '#94a3b8' }}>
                Distribución entre entidades activas: precio P25–P75{' '}
                <strong>{formatRange(distribution?.average_net_unit_price,
                  (value) => formatMoney(value, currency))}</strong>
                {' · '}margen P25–P75{' '}
                <strong>{formatRange(distribution?.margin_percentage, formatPct)}</strong>.
                El margen agregado se calcula desde sumas financieras resueltas.
              </Typography>

              <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#64748b' }}>
                Costes: {formatNumber(quality.resolved_cost_rows)} filas resueltas;
                {' '}{formatNumber(quality.unresolved_cost_rows)} sin resolver.
                Ventana: {result.selection?.start_date ?? '—'} → {result.selection?.end_date ?? '—'}.
              </Typography>

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 800 }}>
                Entidades comparables observadas
              </Typography>
              <TableContainer sx={{ maxHeight: 320, mt: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Entidad</TableCell>
                      <TableCell align="right">Operaciones</TableCell>
                      <TableCell align="right">Precio medio</TableCell>
                      <TableCell align="right">Margen</TableCell>
                      <TableCell align="right">Cobertura coste</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.rows.map((row) => (
                      <TableRow key={row.entity_id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#f8fafc' }}>
                            {row.label}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {row.entity_id}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(row.transaction_rows)}</TableCell>
                        <TableCell align="right">{formatMoney(row.average_net_unit_price, currency)}</TableCell>
                        <TableCell align="right">{formatPct(row.margin_percentage)}</TableCell>
                        <TableCell align="right">{formatPct(row.cost_coverage_pct)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  {formatNumber(result.pagination?.returned_count)} en esta página
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    disabled={previousCursors.length === 0 || query.isFetching}
                    onClick={() => {
                      const previous = previousCursors[previousCursors.length - 1];
                      setPreviousCursors((current) => current.slice(0, -1));
                      setCursor(previous || null);
                    }}
                  >
                    Anterior
                  </Button>
                  <Button
                    size="small"
                    disabled={!result.pagination?.has_more || !result.pagination.next_cursor || query.isFetching}
                    onClick={() => {
                      setPreviousCursors((current) => [...current, cursor ?? '']);
                      setCursor(result.pagination?.next_cursor ?? null);
                    }}
                  >
                    Siguiente
                  </Button>
                </Stack>
              </Stack>
            </>
          )}
        </Box>
      )}
    </Paper>
  );
}
